<template>
  <!-- MAILBOX/PROJECT SWITCHER -->
  <dropdown
    ref="dropdownRef"
    :placement="dropdownPlacement"
    title="Switch To..."
    class="breadcrumb-listing-picker"
    popper-min-width-size="large"
    @open="handleDropdownOpen()"
  >
    <!-- Label -->
    <div class="label" @click="getEntities()">
      <i v-if="iconName" :class="'ddi-' + iconName" class="ddi title-icon" />
      <span class="title">{{ title }}</span>
      <i class="ddi ddi-caret-down" />
    </div>
    <!-- Dropdown -->
    <template v-if="!entitiesLoaded" #content>
      <loading-placeholder :lines="10" />
    </template>
    <template v-else #content>
      <div>
        <sleek-filter-input
          v-if="showFilterInput"
          ref="sleekFilterInputRef"
          v-model="filterQuery"
          placeholder="Find a project or mailbox…"
          show-small-text />
        <ul>
          <!-- Current entity (if applicable) -->
          <li v-if="selectedId" @click="sendToEntityHome(selectedEntity)">
            <i :class="getIconClassForEntity(selectedEntity)" class="ddi" />
            <span class="selected">
              <strong>{{ selectedEntity.title }}</strong>
            </span>
          </li>
          <!-- If tasks, show all tasks first -->
          <template v-if="allTasksSelected">
            <li @click="sendToAllTasks">
              <i class="ddi ddi-task" />
              <span :class="{ 'selected': allTasksSelected }">
                <strong>All Tasks</strong>
              </span>
            </li>
            <li v-if="showMailboxes" @click="sendToAllConversations">
              <i class="ddi ddi-conversation" />
              <span :class="{ 'selected': allConversationsSelected }">
                <strong>All Conversations</strong>
              </span>
            </li>
          </template>
          <template v-else>
            <li v-if="showMailboxes" @click="sendToAllConversations">
              <i class="ddi ddi-conversation" />
              <span :class="{ 'selected': allConversationsSelected }">
                <strong>All Conversations</strong>
              </span>
            </li>
            <li @click="sendToAllTasks">
              <i class="ddi ddi-task" />
              <span :class="{ 'selected': allTasksSelected }">
                <strong>All Tasks</strong>
              </span>
            </li>
          </template>
          <!-- Entities are ordered by favorites, then alphabetically minus the current entity (if applicable) -->
          <li v-for="entity in filteredOptions" :key="entity.id" @click="sendToEntityHome(entity)">
            <i :class="getIconClassForEntity(entity)" class="ddi" />
            <span :class="{ 'selected': isSelected(entity.id) }">
              {{ entity.title }}
            </span>
          </li>
        </ul>
      </div>
    </template>
  </dropdown>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
/* Mixins */
import SearchableDropdownMixin from '@/mixins/searchable-dropdown.mixin'

/* Components */
import StandardFilterInput from '@/components/global/inputs/standard-filter-input.vue'

/* Helpers */

import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { IDashboardOutputProjectsForNavigationProjectForNavigation } from '@/api/types/response.types'

/* API */
import DashboardAPI from '@/api/modules/dashboard.api'

export default defineComponent({
  name: 'BreadcrumbListingPicker',

  components: {
    StandardFilterInput,
  },

  extends: SearchableDropdownMixin,

  props: {
    title: {
      type: String,
      required: true,
    },
    allTasksSelected: {
      type: Boolean,
      default: false,
    },
    allConversationsSelected: {
      type: Boolean,
      default: false,
    },
    iconName: {
      type: String,
      default: null,
    },
    isMailbox: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      entitiesLoaded: false,
      entities: [] as IDashboardOutputProjectsForNavigationProjectForNavigation[],
    }
  },

  computed: {
    totalOptionsCount(): number {
      return this.entities.length
    },
    filteredOptions(): IDashboardOutputProjectsForNavigationProjectForNavigation[] {
      return this.entities.filter(entity => {
        return (
          entity.id !== this.selectedId &&
          entity.title.toLowerCase().includes(this.filterQuery.toLowerCase())
        )
      })
    },
    dropdownPlacement(): string {
      return this.$screen.isTabletOrMobile(this.$mq) ? 'bottom' : 'bottom-start'
    },
    selectedEntity(): IDashboardOutputProjectsForNavigationProjectForNavigation {
      return this.entities.find(e => e.id === this.selectedId)
    },
    showMailboxes(): boolean {
      return this.$permissions.isNotGuest
    },
    showSettingsLink(): boolean {
      return (
        this.isMailbox &&
        this.$permissions.isAdminOrOwner &&
        this.$screen.isTabletOrMobile(this.$mq) &&
        this.selectedId !== null
      )
    },
  },

  methods: {
    getIconClassForEntity(
      entity: IDashboardOutputProjectsForNavigationProjectForNavigation,
    ) {
      if (entity.isFavorite) {
        return 'ddi-star-filled'
      }
      return entity.isMailbox ? 'ddi-mailbox' : 'ddi-project'
    },
    async getEntities() {
      if (this.entitiesLoaded) {
        return
      }
      this.entitiesLoaded = false
      try {
        const response = await DashboardAPI.get_ProjectsForNavigation()
        this.entities = response.listEntities
        this.entitiesLoaded = true
      } catch (error) {
        this.$error.emit(error, true)
      }
    },
    sendToEntityHome(
      entity: IDashboardOutputProjectsForNavigationProjectForNavigation,
    ) {
      if (entity.isMailbox) {
        this.sendToMailbox(entity.id)
      } else {
        this.sendToProject(entity.id)
      }
    },
    sendToMailbox(id: number) {
      RouteAssistant.goToMailbox(this.$router, this.$route.params.accountId, id)
    },
    sendToProject(id: number) {
      RouteAssistant.goToProjectLanding(
        this.$router,
        this.$route.params.accountId,
        id,
        this.$permissions,
      )
    },
    sendToAllTasks() {
      RouteAssistant.goToAllTasks(this.$router, this.$route.params.accountId)
    },
    sendToAllConversations() {
      RouteAssistant.goToAllConversations(
        this.$router,
        this.$route.params.accountId,
      )
    },
  },
})
</script>

<style lang="scss">
.breadcrumb-listing-picker {
  @include single-line-ellipsis;
  max-width: 100%;

  &:hover {
    .title {
      border-bottom: $border--solid-medium;
    }
  }

  .sleek-filter-input {
    padding: 0 4px;
  }

  .label {
    @include single-line-ellipsis;
    color: var(--color-text-secondary);
    font-weight: 500;

    .title {
      font-weight: 500;
    }

    .item-count {
      padding-left: 8px;
      color: var(--color-text-third);
      font-weight: 400;
    }

    .title-icon {
      top: -1px;
      right: -1px;
      margin-right: 6px;
      color: var(--color-text-third);
    }

    .ddi-caret-down {
      margin-left: 6px;
      font-size: 10px;
      top: -1px;
    }
  }
}
</style>
